import React from 'react';
import styled from 'styled-components';
import { BRAND_LOGO, BRAND_NAME } from '../../util/branding';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { userLogout } from '../../sagas/actions';
import { persistor } from '../../redux';
import { mq } from '../../util/breakpoints';


const HeaderWrapper = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #ddd;
  background-color: ${(p) => p.colors.headerBackground};

  ${mq.tablet} {
    padding: 16px;
  }

  /* On phone, keep everything in a row but tighten up */
  ${mq.phone} {
    padding: 12px;
  }
`;

const WelcomeLabel = styled.span`
  font-size: 1rem;
  color: ${(p) => p.colors.headerText};

  ${mq.tablet} {
    font-size: 0.9rem;
  }

  ${mq.phone} {
    font-size: 0.8rem;
  }
`;


// Fit any brand logo into the box the default ISAFE logo (310.7x117.4)
// fills, so square or padded logos don't blow up the header height.
const Logo = styled.img`
  display: block;
  height: 83px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
  object-position: left center;

  ${mq.tablet} {
    height: 68px;
    max-width: 180px;
  }

  ${mq.phone} {
    height: 45px;
    max-width: 120px;
  }
`;

const DateTime = styled.div`
  font-size: 1rem;
  color: ${(p) => p.colors.headerText};

  ${mq.phone} {
    font-size: 0.85rem;
  }
`;

const WelcomeMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
  color: ${(p) => p.colors.headerText};

  ${mq.phone} {
    align-items: flex-end;
    text-align: right;
    gap: 4px;
  }
`;

const Strong = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${(p) => p.colors.headerText};

  ${mq.phone} {
font-size: 0.8rem;  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 5px;
  margin-top: 5px;

  ${mq.phone} {
    gap: 4px;
  }
`;

const Button = styled.button`
  padding: 8px 16px;
  background-color: ${(p) => p.colors.buttonBackground};
  color: ${(p) => p.colors.buttonText};
  border: none;
  cursor: pointer;
  font-weight: bold;
  border-radius: 5px;
  font-size: 0.7em;
  font-family: inherit;
  text-decoration: none !important;

  &:visited {
    color: ${(p) => p.colors.buttonText};
  }

  &:hover {
    background-color: ${(p) => p.colors.buttonHover};
  }

  ${mq.tablet} {
    padding: 5px 10px;
    font-size: 0.7em;
  }

  ${mq.phone} {
    padding: 4px 8px;
    font-size: 0.75em;
  }
`;

const OrgInfo = styled.div`
  // margin-top: 10px;
  color: ${(p) => p.colors.labelText};
  font-weight: 600;
  text-align: right;
  // padding: 8px 10px;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.3;

  ${mq.phone} {
    margin-top: 6px;
    font-size: 0.8rem;
    padding: 6px 8px;
  }
`;


export default function Header({ colors }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const signedIn = useSelector((s) => s.auth.signedIn);
  const userName = useSelector((s) => s.auth.user.first_name);
  const become   = useSelector((s) => s.auth.become);
  const selected = useSelector((s) => s.auth.selected_subscriptions) || {};
  const roles    = useSelector((s) => s.auth.user?.roles) || [];

  const isSubscriptionAdmin = React.useMemo(() => {
    const subscriptionRoleIds = new Set([
      'subscription_access',
      'subscription_accountant',
      'subscription_admin',
      'subscription_implementor',
      'subscription_salesperson',
    ]);
    return roles.some((role) => subscriptionRoleIds.has(role?.id));
  }, [roles]);

  const showSubscriptionControls = become === 1 || isSubscriptionAdmin;

  const dateOptions = { weekday: 'long', year: 'numeric', month: 'short', day: 'numeric' };
  const formattedDate = new Date().toLocaleDateString('en-US', dateOptions);

  const goAccountSettings = () => {
    navigate('/user/update');
  };

  const handleLogoutClick = async () => {
    dispatch(userLogout());
    persistor.purge();
    navigate('/');
    window.location.reload();
  };

  return (
    <HeaderWrapper colors={colors}>
      <a href="https://isafeventures.com/">
        <Logo src={BRAND_LOGO} alt={`${BRAND_NAME} logo`} />
      </a>

      {signedIn ? (
        <WelcomeMessage colors={colors}>
          <div>
              <WelcomeLabel colors={colors}>Welcome,&nbsp;</WelcomeLabel>
            <Strong colors={colors}>{userName}</Strong>
          </div>

          <ButtonContainer>
            {showSubscriptionControls ? (
              <>
                <Button
                  as="a"
                  href="https://isafeventures.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  colors={colors}
                >
                  ISAFE Home
                </Button>
                <Button
                  onClick={goAccountSettings}
                  colors={colors}
                >
                  Account Settings
                </Button>
                <Button onClick={handleLogoutClick} colors={colors}>
                  LOGOUT
                </Button>
              </>
            ) : (
              <>
                <Button onClick={() => navigate('/myinfo')} colors={colors}>
                  MY INFO
                </Button>
                <Button onClick={handleLogoutClick} colors={colors}>
                  LOGOUT
                </Button>
              </>
            )}
          </ButtonContainer>

          {showSubscriptionControls && selected.org_name && (
            <OrgInfo colors={colors}>
              <div>{selected.org_name}</div>
              <div>
                {[selected.org_city, selected.org_state]
                  .filter(Boolean)
                  .join(', ')}
                {selected.org_zip ? ` ${selected.org_zip}` : ''}
              </div>
            </OrgInfo>
          )}
        </WelcomeMessage>
      ) : (
        <DateTime colors={colors}>{formattedDate}</DateTime>
      )}
    </HeaderWrapper>
  );
}
